import type { ReactNode } from 'react';
import { useFormat } from '@paige/format';
import { CompanyName } from '../TestMark';
import type { CustomerMover, CustomerMovers, MoverBand } from './customer-types';
import { bandWords, moverWord } from './customer-words';
import './customers.css';

export interface MoversPanelProps {
  movers: CustomerMovers;
  /** The router's link to a company's page. */
  renderLink: (id: string, children: ReactNode) => ReactNode;
}

/** The bar's order: falling, steady, rising, then new and no pages. */
export const BAND_ORDER: readonly MoverBand[] = [
  'down50',
  'down15',
  'steady',
  'up15',
  'up50',
  'new',
  'none',
];

/** Every band in the bar's order with its count; a band the ledger left out counts nothing. */
export function bandCounts(movers: CustomerMovers): { band: MoverBand; companies: number }[] {
  return BAND_ORDER.map((band) => ({
    band,
    companies: movers.bands.find((b) => b.band === band)?.companies ?? 0,
  }));
}

/** One bar for everyone: a segment a band, as wide as its share. Seven marks at most, whatever the count. */
function BandBar({ movers }: { movers: CustomerMovers }): ReactNode {
  const f = useFormat();
  const bands = bandCounts(movers);
  const total = bands.reduce((sum, b) => sum + b.companies, 0);
  const spoken = bands
    .filter((b) => b.companies > 0)
    .map((b) => `${bandWords[b.band].word} ${f.int(b.companies)}`)
    .join(', ');
  return (
    <>
      <div className="ad-cu-bands" role="img" aria-label={`Companies by change: ${spoken}`}>
        {bands
          .filter((b) => b.companies > 0)
          .map((b) => (
            <span
              key={b.band}
              className={`ad-cu-band ad-cu-band--${b.band}`}
              style={{ flexGrow: b.companies / Math.max(1, total) }}
              title={`${bandWords[b.band].word}: ${f.int(b.companies)}. ${bandWords[b.band].rule}`}
            />
          ))}
      </div>
      <ul className="ad-cu-bandkey">
        {bands.map((b) => (
          <li key={b.band} title={bandWords[b.band].rule}>
            <span className={`ad-cu-bandkey__mark ad-cu-band--${b.band}`} aria-hidden="true">
              {bandWords[b.band].glyph}
            </span>
            <span className="ad-cu-bandkey__count">{f.int(b.companies)}</span>
            {bandWords[b.band].word}
          </li>
        ))}
      </ul>
    </>
  );
}

function Ranked(props: {
  title: string;
  none: string;
  rows: readonly CustomerMover[];
  renderLink: MoversPanelProps['renderLink'];
}): ReactNode {
  const f = useFormat();
  return (
    <div className="ad-cu-ranked">
      <h3>{props.title}</h3>
      {props.rows.length === 0 ? (
        <p className="ad-cu-quiet">{props.none}</p>
      ) : (
        <ol>
          {props.rows.slice(0, 8).map((m) => (
            <li key={m.id}>
              <span className="ad-cu-ranked__name">
                {props.renderLink(m.id, <CompanyName name={m.name} test={m.testCompany} />)}
              </span>
              <span className="ad-cu-ranked__pages">{f.int(m.pages)}</span>
              <span className="ad-cu-ranked__change">{moverWord(m, (n) => f.int(n))}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * Who is moving: pages read in the last 30 days against the 30 before. Everyone is counted in one of seven
 * bands (one bar, never a mark a company), and the eight companies growing most and slowing most are named.
 */
export function MoversPanel({ movers, renderLink }: MoversPanelProps): ReactNode {
  return (
    <section className="ad-cu-movers" aria-label="Who is moving">
      <header className="ad-cu-movers__head">
        <h2>Who is moving</h2>
        <p>Pages read in the last 30 days against the 30 before.</p>
      </header>
      <BandBar movers={movers} />
      <div className="ad-cu-movers__lists">
        <Ranked
          title="Growing most"
          none="Nobody is growing."
          rows={movers.growing}
          renderLink={renderLink}
        />
        <Ranked
          title="Slowing most"
          none="Nobody is slowing."
          rows={movers.slowing}
          renderLink={renderLink}
        />
      </div>
    </section>
  );
}
